import { httpsCallable } from 'firebase/functions';
import { useState } from 'react';
import { functions } from '../../lib/firebase';

/**
 * generateAssignments callable hook (Sprint BG-DataSettings 10、Phase 5、GAP-A11 + F16)。
 *
 * `useDistributeVerification` と同 pattern (idle / pending / success / error)。
 * 戦略名は snake_case (random_per_user / round_robin / all_users)。
 */

export type GenerateAssignmentsStrategy = 'random_per_user' | 'round_robin' | 'all_users';

export interface GenerateAssignmentsInput {
  districtId: string;
  templateId: string;
  strategy: GenerateAssignmentsStrategy;
  postIds: string[];
  userUids: string[];
  constraints: {
    excludeOwnPosts: boolean;
    maxPerUser?: number;
    /** R-70 P4 (REQ-4.3.53): random_per_user で各 user に最低この件数の自分の投稿を含める。excludeOwnPosts とは排他。 */
    minOwnPosts?: number;
    /** R-70 P4: true なら空き枠を自分の投稿で優先的に埋める (セルフ検証重視)。random_per_user のみ。 */
    preferOwnPosts?: boolean;
    /**
     * REQ-4.3.20: true なら各参加者に「その参加者が参加したセッションの投稿」だけを配る。
     * セッション未紐付けの投稿は誰にでも配る (旧2 踏襲)。全戦略で有効。
     */
    sameSessionOnly?: boolean;
  };
  /** REQ-4.3.20: 対象セッション (空 / 未指定は地区全体)。生成される配分セットに保存される。 */
  sessionIds?: string[];
  displayName?: string;
  description?: string;
}

export interface GenerateAssignmentsResult {
  assignmentSetId: string;
  assignmentCount: number;
  perUserCount: Record<string, number>;
  perPostCount: Record<string, number>;
  strategy: GenerateAssignmentsStrategy;
}

export type UseGenerateAssignmentsState =
  | { status: 'idle'; result: null; error: null }
  | { status: 'pending'; result: null; error: null }
  | { status: 'success'; result: GenerateAssignmentsResult; error: null }
  | { status: 'error'; result: null; error: Error };

export interface UseGenerateAssignmentsReturn {
  state: UseGenerateAssignmentsState;
  call: (input: GenerateAssignmentsInput) => Promise<GenerateAssignmentsResult | null>;
  reset: () => void;
}

export function useGenerateAssignments(): UseGenerateAssignmentsReturn {
  const [state, setState] = useState<UseGenerateAssignmentsState>({
    status: 'idle',
    result: null,
    error: null,
  });

  async function call(input: GenerateAssignmentsInput): Promise<GenerateAssignmentsResult | null> {
    setState({ status: 'pending', result: null, error: null });
    try {
      const fn = httpsCallable<GenerateAssignmentsInput, GenerateAssignmentsResult>(
        functions,
        'generateAssignments',
      );
      const res = await fn(input);
      setState({ status: 'success', result: res.data, error: null });
      return res.data;
    } catch (e) {
      const err = e as Error;
      setState({ status: 'error', result: null, error: err });
      return null;
    }
  }

  function reset() {
    setState({ status: 'idle', result: null, error: null });
  }

  return { state, call, reset };
}
